import React, { useEffect } from 'react';
import { cx } from '@emotion/css';
import { MessageBanner } from '@/components';

import { Accordion, AccordionItem, NavBarBackButton, TitleSmallEmphasized } from '@/design-system';
import infoIcon from '@/assets/info-icon.svg';
import { ChildNewModel } from '@/hooks/useProfile';
import { useReactiveVar } from '@apollo/client';
import { districtVar } from '@/graphql/state';
import { useLocation } from 'react-router-dom';
import { useNavigationPreviousPage } from '@/hooks';
import ChangeEmailForm from './components/ChangeEmailForm/ChangeEmailForm';
import {
  ChildGroupByCommunity,
  InstitutionFromSearch,
  GradeChangeInput,
  PendingSchools,
} from './AccountInformation.types';
import { bannersMessages } from './AccountInformation.constants';
import styles from './AccountInformation.styles';
import EmailNotificationsBanner from './components/EmailNotificationsBanner/EmailNotificationsBanner';
import { SchoolInformation } from './SchoolInformation';
import { Community } from './Community';
import { FollowingOrganizations } from './FollowingOrganizations';

type AccountInformationProps = {
  userId: number | null;
  email: string;
  managedBy: string;
  childrenGrouped: ChildGroupByCommunity[];
  pendingSchools: PendingSchools[];
  childrenList: ChildNewModel[] | null;
  childrenAddingIds: Array<string>;
  accountManagementEnabled: boolean;
  isNotificationEnabled: boolean;
  revertBlocked: boolean;
  updateRevertBlock: () => void;
  changeManagedByStatus: () => void;
  lookUpSchools: (options, callback) => void;
  handleSubmit: (childUId: string) => Promise<void> | void;
  handleSchoolChange: (
    value: InstitutionFromSearch & { schoolId: number; districtId: number },
    id: number
  ) => void;
  handleGradeChange: (input: GradeChangeInput, childUId: string) => void;
  handleRemoveChild: (childUId: string) => Promise<void> | void;
  handleRemoveAddedChild: (childUId: string) => void;
  handleAddChild: () => void;
  handleCancel: (childUId: string) => void;
  handleDismissPendingNotification: (guardianId: number) => void;
  childUidToFocus: string | null;
  onChildFocused: () => void;
};

export const AccountInformation: React.FC<AccountInformationProps> = ({
  userId,
  email,
  managedBy,
  childrenGrouped,
  pendingSchools,
  childrenList,
  childrenAddingIds,
  accountManagementEnabled,
  isNotificationEnabled,
  revertBlocked,
  updateRevertBlock,
  changeManagedByStatus,
  lookUpSchools,
  handleSubmit,
  handleSchoolChange,
  handleGradeChange,
  handleRemoveChild,
  handleRemoveAddedChild,
  handleAddChild,
  handleCancel,
  handleDismissPendingNotification,
  childUidToFocus,
  onChildFocused,
}) => {
  const district = useReactiveVar(districtVar);
  const location = useLocation();
  const navigatePreviousPage = useNavigationPreviousPage();
  const expandSection = (location.state as { expandSection?: string } | null)?.expandSection;

  useEffect(() => {
    if (!expandSection) return undefined;

    const frame = requestAnimationFrame(() => {
      const item = document.getElementById(expandSection);
      const trigger = item?.querySelector<HTMLButtonElement>('button[aria-controls]');
      if (!trigger) return;
      trigger.scrollIntoView({ behavior: 'smooth', block: 'start' });
      trigger.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(frame);
  }, [expandSection]);

  return (
    <>
      <NavBarBackButton trackId="account_information_page" onClickBackButton={navigatePreviousPage}>
        Account Information
      </NavBarBackButton>
      <main>
        <div className={styles.headerSpacing}>
          <fieldset className={cx(styles.horizontalPadding, styles.districtGroup)}>
            <legend className={styles.legend}>
              <TitleSmallEmphasized id="parent-account-label" className={styles.heading} as="h2">
                Parent Account
              </TitleSmallEmphasized>
            </legend>
            <div className={styles.changeEmailContainer}>
              <ChangeEmailForm email={email} updateRevertBlock={updateRevertBlock} />
              <EmailNotificationsBanner isNotificationEnabled={isNotificationEnabled} />
            </div>
            {district?.isMultiDistrict && (
              <MessageBanner
                subheadline={bannersMessages.multiDistrict}
                iconSrc={infoIcon}
                variant="warning"
                dataTestId="multiDistrictAccount"
              />
            )}
          </fieldset>
        </div>
        <div className={styles.accordionContainer}>
          <Accordion headingLevel={2} defaultExpanded={expandSection ? [expandSection] : []}>
            <AccordionItem id="school-information" title="School Information">
              <SchoolInformation
                userId={userId}
                managedBy={managedBy}
                childrenGrouped={childrenGrouped}
                pendingSchools={pendingSchools}
                childrenList={childrenList}
                childrenAddingIds={childrenAddingIds}
                accountManagementEnabled={accountManagementEnabled}
                revertBlocked={revertBlocked}
                changeManagedByStatus={changeManagedByStatus}
                lookUpSchools={lookUpSchools}
                handleSubmit={handleSubmit}
                handleSchoolChange={handleSchoolChange}
                handleGradeChange={handleGradeChange}
                handleRemoveChild={handleRemoveChild}
                handleRemoveAddedChild={handleRemoveAddedChild}
                handleAddChild={handleAddChild}
                handleCancel={handleCancel}
                handleDismissPendingNotification={handleDismissPendingNotification}
                childUidToFocus={childUidToFocus}
                onChildFocused={onChildFocused}
              />
            </AccordionItem>
            <AccordionItem id="community-information" title="Community Information">
              <Community />
            </AccordionItem>
            <AccordionItem id="following-organizations" title="Following Organizations">
              <FollowingOrganizations />
            </AccordionItem>
          </Accordion>
        </div>
      </main>
    </>
  );
};
